
import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Guide } from "@/types/guide";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import MainLayout from "@/components/layout/MainLayout";

const GuideDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();

  const { data: guide, isLoading } = useQuery({
    queryKey: ["guide", id],
    queryFn: async () => {
      if (!id) throw new Error("No guide ID provided");
      
      const { data, error } = await supabase
        .from("guides")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Guide;
    },
    enabled: !!id,
  });

  // Display the title, summary and content based on language
  const getTitle = () => {
    if (!guide) return "";
    if (language === 'en' && guide.title_en) return guide.title_en;
    return guide.title;
  };

  const getSummary = () => {
    if (!guide) return "";
    if (language === 'en' && guide.summary_en) return guide.summary_en;
    return guide.summary;
  };

  const getContent = () => {
    if (!guide) return "";
    if (language === 'en' && guide.content_en) return guide.content_en;
    return guide.content;
  };

  const getTags = () => {
    if (!guide) return [];
    if (language === 'en' && guide.tags_en && guide.tags_en.length > 0) return guide.tags_en;
    return guide.tags || [];
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded mb-4"></div>
              <div className="h-64 bg-gray-200 rounded mb-6"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!guide) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-4">
                {language === 'en' ? 'Guide not found' : '指南未找到'}
              </h1>
              <Link to="/guides">
                <Button variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {language === 'en' ? 'Back to guides' : '返回指南列表'}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Navigation */}
          <div className="mb-6">
            <Link to="/guides">
              <Button variant="outline" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {language === 'en' ? 'Back to guides' : '返回指南列表'}
              </Button>
            </Link>
          </div>

          {/* Article */}
          <article className="max-w-4xl mx-auto">
            {/* Cover Image */}
            {guide.cover_image_url && (
              <div className="aspect-video w-full overflow-hidden rounded-lg mb-8">
                <img
                  src={guide.cover_image_url}
                  alt={getTitle()}
                  className="object-cover w-full h-full"
                />
              </div>
            )}

            {/* Header */}
            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                {getTitle()}
              </h1>
              
              {/* Summary */}
              {getSummary() && (
                <p className="text-muted-foreground text-base mb-6 leading-relaxed">
                  {getSummary()}
                </p>
              )}

              {/* Meta */}
              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <Calendar className="mr-2 h-4 w-4" />
                <time dateTime={guide.created_at}>
                  {new Date(guide.created_at).toLocaleDateString(language === 'en' ? 'en-CA' : 'zh-TW')}
                </time>
              </div>

              {/* Tags */}
              {getTags().length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {getTags().map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {/* Content */}
            <Card>
              <CardContent className="p-8">
                <div 
                  className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground"
                  dangerouslySetInnerHTML={{ __html: getContent() }}
                />
              </CardContent>
            </Card>
          </article>
        </div>
      </div>
    </MainLayout>
  );
};

export default GuideDetail;
